// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { selectTagsForObject } from '@shared/data-access/slices/tags-management';
import type { RootTagsManagementState } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { Chip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

const VISIBLE_TAGS_MAX = 3;

const TAG_NAME_MAX_LENGTH = 12;

export interface ObjectRowTagsProps {
  readonly objectType: string;

  readonly objectId: string;
}

export const ObjectRowTags = ({ objectType, objectId }: ObjectRowTagsProps) => {
  const tags = useSelector((state: RootTagsManagementState) =>
    selectTagsForObject(state, objectType, objectId),
  );

  const visible = tags.slice(0, VISIBLE_TAGS_MAX);

  const restCount = tags.length - VISIBLE_TAGS_MAX;

  const truncate = (name: string) =>
    name.length > TAG_NAME_MAX_LENGTH
      ? `${name.slice(0, TAG_NAME_MAX_LENGTH)}…`
      : name;

  if (tags.length === 0) return <div />;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map(tag => (
        <Chip
          key={tag.tag_id}
          label={truncate(tag.name)}
          size="small"
          color="primary"
          variant="filled"
          className="!max-w-[120px]"
          sx={{
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      ))}
      {restCount > 0 && (
        <Typography
          component="span"
          variant="caption"
          className="!text-primary-medium"
        >
          +{restCount}
        </Typography>
      )}
    </div>
  );
};
